import { BigNumber } from '@0x/utils'
import assetUtils, { Asset, getCommon } from 'app-lib/assetUtils'
import loanUtils from 'app-lib/loanUtils'
import providerUtils from 'app-lib/providerUtils'
import swapRateUtils from 'app-lib/swapRateUtils'
import tradeUtils from 'app-lib/tradeUtils'
import appConfig, { chainConfigs } from 'bzx-common/src/config/appConfig'
import IBorrowedFundsState from 'bzx-common/src/domain/IBorrowedFundsState'
import ManageCollateralRequest from 'bzx-common/src/domain/ManageCollateralRequest'
import oracleApi from 'bzx-common/src/lib/apis/oracleApi'
import { DEX } from 'bzx-common/src/lib/dexSelector/types'
import numberUtils, { formatPercent } from 'bzx-common/src/lib/numberUtils'
import { RootStore } from 'bzx-common/src/stores'
import _ from 'lodash'
import React, { Component, FormEvent } from 'react'
import { Button, Form, Icon, InfoTip, Input, Slider } from 'ui-framework'
import fulcrumProvider from '../../../../fulcrum/src/services/FulcrumProvider'
import ExpectedResult from '../ExpectedResult'

export interface IManageCollateralFormProps {
  loan: IBorrowedFundsState
  request: ManageCollateralRequest
  isBorrow?: boolean
  rootStore: RootStore
  closeModal: () => void
}

interface IManageCollateralFormState {
  minValue: number
  maxValue: number
  loanValue: BigNumber
  selectedValue: BigNumber
  assetBalance: BigNumber
  collateralAmount: BigNumber
  collateralExcess: BigNumber
  collateralizedPercent: BigNumber
  collateralToLoanRate: BigNumber
  collateralTooLow: boolean
  inputAmount: string
  isLoading: boolean
  liquidationPrice: BigNumber
  activeTokenLiquidation: Asset
  needsApproval: boolean
  spender: string
}

export default class ManageCollateralForm extends Component<
  IManageCollateralFormProps,
  IManageCollateralFormState
> {
  private _onInputUpdate = _.debounce((value) => this.getExpectedData(value), appConfig.delay)
  public _collateralPrecision = assetUtils.getDecimals(
    fulcrumProvider.currentChainId,
    this.props.loan.collateralAsset
  )
  constructor(props: IManageCollateralFormProps) {
    super(props)

    this.state = {
      minValue: 0,
      maxValue: 0,
      selectedValue: new BigNumber(0),
      loanValue: new BigNumber(0),
      assetBalance: new BigNumber(0),
      collateralAmount: new BigNumber(0),
      collateralExcess: new BigNumber(0),
      collateralizedPercent: new BigNumber(0),
      collateralToLoanRate: new BigNumber(0),
      collateralTooLow: false,
      inputAmount: '',
      isLoading: true,
      liquidationPrice: new BigNumber(0),
      activeTokenLiquidation: this.props.loan.collateralAsset,
      needsApproval: false,
      spender: '',
    }
  }

  public async componentDidMount() {
    const chain = chainConfigs[fulcrumProvider.currentChainId]
    const collateralState = { minValue: 0.9 * 10 ** 18, maxValue: 3 * 10 ** 20, currentValue: 0 }
    this.setState({
      minValue: collateralState.minValue,
      maxValue: collateralState.maxValue,
    })
    const { loan } = this.props

    let collateralExcess = new BigNumber(0)
    try {
      collateralExcess = await tradeUtils.getManageCollateralExcessAmount(fulcrumProvider, loan)
    } catch (err) {
      console.error('getManageCollateralExcessAmount fail', err)
    }

    // check balance
    const assetBalance = await providerUtils.getTokenBalanceOfUser(
      fulcrumProvider,
      this.props.loan.collateralAsset
    )

    const collateralizedPercent = loan.collateralizedPercent
      .multipliedBy(100)
      .plus(this.props.isBorrow ? 100 : 0)

    let minCollateral
    let maxCollateral

    minCollateral = loan.collateralAmount.minus(collateralExcess).times(collateralState.minValue)

    maxCollateral = minCollateral
      .times(collateralState.maxValue - collateralState.minValue)
      .dividedBy(10 ** 20)

    // set range for case when loan has zero margin. Actually it don't affect slider range.
    // TODO: review the logic of the component
    if (loan.loanData.collateral.isZero()) {
      minCollateral = new BigNumber(loan.loanData.maintenanceMargin.div(10 ** 18).plus(5))
      maxCollateral = new BigNumber(loan.loanData.maintenanceMargin.div(10 ** 18).plus(50))
    }

    const currentCollateral = loan.collateralAmount.times(10 ** 18)

    if (maxCollateral.lt(currentCollateral)) {
      maxCollateral = currentCollateral
    }

    // new_v = (new_max - new_min) / (old_max - old_min) * (v - old_min) + new_min
    let currentCollateralNormalizedBN = new BigNumber(
      collateralState.maxValue - collateralState.minValue
    )
      .dividedBy(maxCollateral.minus(minCollateral))
      .times(currentCollateral.minus(minCollateral))
      .plus(collateralState.minValue)

    if (
      currentCollateralNormalizedBN
        .dividedBy(collateralState.maxValue - collateralState.minValue)
        .lte(0.01)
    ) {
      currentCollateralNormalizedBN = new BigNumber(collateralState.minValue)
    }

    let assetBalanceNormalizedBN = new BigNumber(
      collateralState.maxValue - collateralState.minValue
    )
      .dividedBy(maxCollateral.minus(minCollateral))
      .times(assetBalance.minus(minCollateral))
      .plus(collateralState.minValue)

    if (
      assetBalanceNormalizedBN
        .dividedBy(collateralState.maxValue - collateralState.minValue)
        .lte(0.01)
    ) {
      assetBalanceNormalizedBN = new BigNumber(collateralState.minValue)
    }

    const isUsingUniV3 = !chain.dexes.some((dex) => dex === DEX.SUSHI)
    let collateralToLoanRate = new BigNumber(NaN)

    if (!isUsingUniV3) {
      collateralToLoanRate = (
        await swapRateUtils.getActualSwapRate(fulcrumProvider, {
          srcToken: loan.collateralAsset,
          destToken: loan.loanAsset,
          loanDataBytes: '0x',
        })
      ).rate
    } else {
      // At least temporarily; using oracle price instead of AMM for chain that dont have sushi
      collateralToLoanRate = await oracleApi.getSwapRate(
        fulcrumProvider,
        loan.collateralAsset,
        loan.loanAsset
      )
    }

    const liquidationPrice = loanUtils.getLiquidationPrice(
      fulcrumProvider,
      this.props.loan,
      this.props.loan.loanData.collateral,
      this.state.activeTokenLiquidation
    )

    const collateralTooLow = loan.collateralizedPercent
      .multipliedBy(100)
      .minus(10)
      .lt(this.props.loan.loanData.maintenanceMargin.div(10 ** 18))

    const { checkApprovals } = this.props.rootStore.spendingApprovals
    const spender = chainConfigs[fulcrumProvider.currentChainId].contracts.bzx.toLowerCase()

    await checkApprovals([loan.collateralAsset], spender)

    this.setState({
      spender,
      collateralToLoanRate,
      loanValue: currentCollateralNormalizedBN,
      selectedValue: currentCollateralNormalizedBN,
      collateralizedPercent,
      collateralExcess,
      collateralTooLow,
      assetBalance,
      liquidationPrice,
      inputAmount: '0',
      isLoading: false,
    })
  }

  public render() {
    const warningMessage = this.state.assetBalance.lt(this.state.inputAmount)
      ? `Insufficient ${this.props.loan.collateralAsset} balance in your wallet!`
      : this.state.selectedValue.isZero()
      ? 'Too small collateralization'
      : this.state.loanValue.eq(this.state.maxValue) && new BigNumber(this.state.inputAmount).gt(0)
      ? 'Too big collateralization'
      : undefined

    const canSubmit = this.state.loanValue !== this.state.selectedValue && !warningMessage

    const { spriteLogo } = assetUtils.getCommon(this.props.loan.loanAsset)
    const { loan, request } = this.props
    const loanStatus = this.state.collateralTooLow ? 'risky' : 'safe'
    const submiTitle = this.state.collateralAmount.toFixed(this._collateralPrecision, 1)
    const { displayName } = getCommon(loan.collateralAsset)

    return (
      <Form onCancel={this.props.closeModal} onSubmit={this.onSubmitClick}>
        <section className="form__left_block">
          <div className="form__info_block">
            <div className="form__info_block__logo">
              <Icon name={spriteLogo} size="auto" className="form__info_block__logo_svg" />
            </div>
            <div className="flex-col width-100p">
              <div className="txt-title txt-default form--asset">Manage Collateral</div>
              <div className="borrow-flex--column-row">
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1 margin-right-05">
                    <div className="txt-alternate txt-muted">APR</div>
                    <div className="txt-default txt-subtitle">
                      {formatPercent(loan.interestRate)}%
                    </div>
                  </div>
                  <InfoTip id="repay-loan-apr-tooltip" top={-3}>
                    The % interest paid per year on your position.
                  </InfoTip>
                </div>
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1">
                    <div className="txt-alternate txt-muted margin-right-1ch">{displayName}</div>
                    <div className="txt-default txt-subtitle">
                      {numberUtils.abbreviate(this.state.assetBalance)}
                    </div>
                  </div>
                  <Icon name="wallet-balance" size={20} top={-3} color="var(--color-txt-muted)" />
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="form__content">
          <div className="grid-2-col">
            <div className="flex-column">
              <div className="txt-subtitle margin-bottom-05">Current Collateral</div>
              <div className="txt-title padded-v-1">
                {numberUtils.autoFormat(request.collateralAmount)} {displayName}
              </div>
            </div>
            <div className="flex-column">
              <div className="txt-subtitle margin-bottom-05">
                You will {this.state.loanValue.gt(this.state.selectedValue) ? 'withdraw' : 'top up'}
              </div>
              <div className=" flex-between">
                <div className="txt-title padded-v-1">{displayName} </div>
                <div className="form__input-container margin-left-1">
                  <Input
                    id="withdraw"
                    className={`width-100p${this.state.collateralTooLow ? 'txt-negative' : ''}`}
                    name="withdraw-input"
                    type="text"
                    value={this.state.inputAmount}
                    onChange={this.onCollateralChange}
                    onChangeEmit="value"
                    isLoading={this.state.isLoading}
                    withLoader
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="margin-v-1">
            <div className="flex-between margin-bottom-1">
              <span className="form__label-safe txt-subtitle-super">Riskier</span>
              <span className="form__label-safe txt-subtitle-super">Safer</span>
            </div>
            <Slider
              step={0.01}
              type="colored"
              min={this.state.minValue}
              max={this.state.maxValue}
              value={this.state.selectedValue.toNumber()}
              onChange={this.onChange}
              onAfterChange={this.onAfterChange}
            />
          </div>

          <div className="margin-v-1">
            <ExpectedResult
              collaterizationPercents={this.state.collateralizedPercent}
              liquidationPrice={this.state.liquidationPrice}
              loanToken={loan.loanAsset}
              collateralToken={loan.collateralAsset}
              loanStatus={loanStatus}
              chainId={fulcrumProvider.currentChainId}
            />
          </div>
          {warningMessage && !this.state.isLoading ? (
            <p className="form__warning">{warningMessage}</p>
          ) : this.state.needsApproval ? (
            <Button
              variant="green"
              className="block"
              isLoading={this.state.isLoading}
              onClick={this.approveSpending}>
              Approve spending
            </Button>
          ) : (
            <Button
              type="submit"
              variant="green"
              isLoading={this.state.isLoading}
              disabled={!canSubmit}
              title={submiTitle}>
              {this.state.loanValue.gt(this.state.selectedValue) ? `Withdraw -` : 'Top Up '}
              {numberUtils.autoFormat(this.state.collateralAmount)} {loan.collateralAsset}
            </Button>
          )}
        </section>
      </Form>
    )
  }

  public approveSpending = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    this.setState({ isLoading: true })
    const success = await this.props.rootStore.spendingApprovals.setInfiniteApprovalForToken(
      this.props.loan.collateralAsset,
      this.state.spender
    )
    if (success) {
      const { needsApproval } = this.props.rootStore.spendingApprovals
      this.setState({
        needsApproval: needsApproval(
          fulcrumProvider.currentChainId,
          this.props.loan.collateralAsset,
          this.state.spender,
          this.state.collateralAmount
        ),
      })
    }
    this.setState({ isLoading: false })
  }

  public onSubmitClick = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const { isBorrow, request, rootStore } = this.props
    const { borrowStore, tradeStore } = rootStore

    request.isWithdrawal = this.state.loanValue.gt(this.state.selectedValue)
    request.collateralAmount = new BigNumber(this.state.collateralAmount)
    isBorrow ? borrowStore.onLoanConfirmed(request) : tradeStore.manageCollateral(request)
  }

  private onChange = (value: number) => {
    const collateralAmount = this.getCollateralAmount(new BigNumber(value))
    const amount = numberUtils.formatPrecision(collateralAmount)
    const isWithdrawal = this.state.loanValue.gt(value)
    const inputAmountText = isWithdrawal ? '-' + amount : amount

    this.setState({
      inputAmount: inputAmountText,
      selectedValue: new BigNumber(value),
      collateralAmount,
      collateralizedPercent: new BigNumber(0),
      liquidationPrice: new BigNumber(0),
    })
  }

  private onAfterChange = () => {
    this._onInputUpdate(this.state.collateralAmount)
  }

  public onCollateralChange = (value: string) => {
    const bnValue = new BigNumber(value)
    const roundedValue = bnValue.absoluteValue()

    const decimals = assetUtils.getDecimals(
      fulcrumProvider.currentChainId,
      this.props.loan.loanAsset
    )
    // collateral amount enough to get maintenance margin + 200%
    const maxCollateralForZeroMarginLoan =
      this.props.loan.loanData.principal
        .div(10 ** decimals)
        .times(this.props.loan.loanData.maintenanceMargin.div(10 ** 20).plus(3))
        .div(this.state.collateralToLoanRate) || new BigNumber(0)

    let selectedValue = bnValue.lt(0)
      ? new BigNumber(this.state.loanValue).minus(
          roundedValue.dividedBy(this.state.collateralExcess).multipliedBy(this.state.loanValue)
        )
      : roundedValue
          .dividedBy(
            this.props.loan.collateralAmount.isZero()
              ? maxCollateralForZeroMarginLoan
              : this.props.loan.collateralAmount
          )
          .multipliedBy(new BigNumber(this.state.maxValue).minus(this.state.loanValue))
          .plus(this.state.loanValue)

    selectedValue = selectedValue.gt(0) ? selectedValue : new BigNumber(0)
    const collateralAmount = this.getCollateralAmount(selectedValue)
    this.setState({
      collateralAmount,
      selectedValue,
      collateralizedPercent: new BigNumber(0),
      liquidationPrice: new BigNumber(0),
      inputAmount: value,
    })
    this._onInputUpdate(collateralAmount)
  }

  private getCollateralAmount = (value: BigNumber) => {
    const decimals = assetUtils.getDecimals(
      fulcrumProvider.currentChainId,
      this.props.loan.loanAsset
    )
    // collateral amount enough to get maintenance margin + 200%
    const maxCollateralForZeroMarginLoan = this.props.loan.loanData.principal
      .div(10 ** decimals)
      .times(this.props.loan.loanData.maintenanceMargin.div(10 ** 20).plus(3))
      .div(this.state.collateralToLoanRate)

    return value.lt(this.state.loanValue)
      ? new BigNumber(this.state.loanValue)
          .minus(value)
          .dividedBy(this.state.loanValue)
          .multipliedBy(this.state.collateralExcess)
      : value.gt(this.state.loanValue)
      ? new BigNumber(value)
          .minus(this.state.loanValue)
          .dividedBy(new BigNumber(this.state.maxValue).minus(this.state.loanValue))
          .multipliedBy(
            this.props.loan.collateralAmount.isZero()
              ? maxCollateralForZeroMarginLoan
              : this.props.loan.collateralAmount
          )
      : new BigNumber(0)
  }

  private getExpectedData = async (value: BigNumber) => {
    const { activeTokenLiquidation, collateralAmount, spender, loanValue, selectedValue } =
      this.state
    const result = await loanUtils.getEstimateDataChangeEstimate(
      fulcrumProvider,
      this.props.loan,
      value,
      selectedValue.lt(loanValue),
      activeTokenLiquidation
    )

    const collateralizedPercent = result.collateralizedPercent.minus(this.props.isBorrow ? 0 : 100)
    const collateralTooLow = result.collateralizedPercent
      .minus(110)
      .lt(this.props.loan.loanData.maintenanceMargin.div(10 ** 18))
    const isWithdrawal = loanValue.gt(selectedValue)
    const { spendingApprovals } = this.props.rootStore

    const needsApproval =
      !isWithdrawal &&
      spender !== '' &&
      spendingApprovals.needsApproval(
        fulcrumProvider.currentChainId,
        this.props.loan.collateralAsset,
        spender,
        collateralAmount
      )

    this.setState({
      needsApproval,
      collateralizedPercent,
      collateralTooLow,
      liquidationPrice: result.liquidationPrice,
    })
  }
}
